import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

interface OutOfCreditsModalProps {
  isOpen: boolean;
  // Without onClose it can't be dismissed (the Locked workspace popup).
  onClose?: () => void;
  title?: string;
  description?: string;
  benefits?: string[];
  // Null hides the button.
  ctaLabel?: string | null;
  className?: string;
}

const CheckIcon = () => (
  <svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.076 0L4.98549 8.59528L1.18859 5.11205L0 6.4052L4.4022 10.4442L5.0075 11L5.6018 10.4387L15.2866 1.28214L14.076 0Z" fill="black"/>
  </svg>
);

export function OutOfCreditsModal({
  isOpen,
  onClose,
  title = "You're out of credits",
  description = 'Upgrade your plan to continue creating ad designs.',
  benefits = [
    'Get more credits every month',
    'Generate unlimited ad designs',
    'Access premium templates',
  ],
  ctaLabel = 'Upgrade now',
  className = '',
}: OutOfCreditsModalProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle open/close animations
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else if (isVisible) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div 
      className={`delete-modal is-upgrade ${className} ${isVisible ? 'is-visible' : ''} ${isAnimating ? 'is-open' : ''} ${!isAnimating && isVisible ? 'is-closing' : ''}`}
    >
      <div className="upgrade-modal-wrapper">
        <div className="upgrade-gradient-bg"></div>
        <div className="white-background is-upgrade-modal" ref={modalRef}>
        {onClose && (
          <div
            className="modal-close"
            onClick={onClose}
            style={{ cursor: 'pointer' }}
          >
            <div className="rotated-text">+</div>
          </div>
        )}
        
        <div className="upgrade-modal-content">
          <img 
            src="/images/lima-logomark-1_1lima-logomark-1.avif" 
            alt="Lima" 
            className="upgrade-logo"
          />
          
          <div className="text-size-large text-weight-semibold max-width-xsmall">{title}</div>
          <div className="spacer-tiny"></div>
          <div className="text-size-regular text-colour-alt-grey max-width-xsmall">
            {description}
          </div>
          
          {benefits.length > 0 && (
            <>
              <div className="spacer-small"></div>

              <div className="upgrade-benefits-box">
                {benefits.map((benefit, index) => (
                  <div key={benefit}>
                    <div className="upgrade-benefit-row">
                      <CheckIcon />
                      <span>{benefit}</span>
                    </div>
                    {index < benefits.length - 1 && <div className="upgrade-benefit-divider"></div>}
                  </div>
                ))}
              </div>
            </>
          )}

          {ctaLabel && (
            <>
              <div className="spacer-medium"></div>

              <Link
                to="/plans"
                className="button w-button"
                onClick={onClose}
              >
                {ctaLabel}
              </Link>
            </>
          )}
        </div>
      </div>
      </div>
      
      {onClose && (
        <div
          className="close-layer"
          onClick={onClose}
          style={{ cursor: 'pointer' }}
        ></div>
      )}
    </div>
  );
}
